'use client';

import { svgShapes } from '@/utils/svgShapes';
import React from 'react';
import ColorSchemeSelect from './ColorSchemeSelect';

const SHAPE_OPTIONS = Object.keys(svgShapes);
const SHAPE_NONE_VALUE = 'none';
const COLOR_FILL_GROUPS = [
    {
        label: 'Primary',
        options: [
            { value: 'light-primary', label: 'Light Primary' },
            { value: 'mid-primary', label: 'Mid Primary' },
            { value: 'primary', label: 'Primary' },
            { value: 'dark-primary', label: 'Dark Primary' },
            { value: 'darker-primary', label: 'Darker Primary' },
        ],
    },
    {
        label: 'Secondary',
        options: [
            { value: 'light-secondary', label: 'Light Secondary' },
            { value: 'mid-secondary', label: 'Mid Secondary' },
            { value: 'secondary', label: 'Secondary' },

            { value: 'dark-secondary', label: 'Dark Secondary' },
            { value: 'darker-secondary', label: 'Darker Secondary' },
        ],
    },
    {
        label: 'Neutral',
        options: [
            { value: 'white', label: 'White' },
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' },
        ],
    },
];

function fillColorToSwatch(value = '') {
    const map = {
        primary: 'bg-primary-800',
        'mid-primary': 'bg-primary-600',
        'light-primary': 'bg-primary-100',
        'dark-primary': 'bg-primary-900',
        'darker-primary': 'bg-primary-950',
        secondary: 'bg-secondary-800',
        'mid-secondary': 'bg-secondary-600',
        'light-secondary': 'bg-secondary-100',
        'dark-secondary': 'bg-secondary-900',
        'darker-secondary': 'bg-secondary-950',
        light: 'bg-neutral-100',
        dark: 'bg-neutral-800',
        white: 'bg-white',
        default: 'bg-white',
    };
    return map[String(value).trim()] ?? 'bg-neutral-200';
}

function titleize(token = '') {
    return token
        .replace(/[-_]+/g, ' ')
        .replace(/\b\w/g, (m) => m.toUpperCase());
}

function Toggle({ checked, onChange, ariaLabel }) {
    return (
        <label className="relative inline-flex cursor-pointer items-center">
            <input
                type="checkbox"
                className="peer sr-only"
                checked={checked}
                onChange={(e) => onChange?.(e.target.checked)}
                aria-label={ariaLabel}
            />
            <div
                className="peer h-5 w-9 rounded-full bg-neutral-300
        after:absolute after:left-[3px] after:top-0.5 after:h-4 after:w-4
        after:rounded-full after:border after:border-gray-300 after:bg-white
        after:transition-all after:content-['']
        peer-checked:bg-primary-600 peer-checked:after:translate-x-4 peer-checked:after:border-white
        peer-focus:ring-1 peer-focus:ring-blue-300"
            />
        </label>
    );
}

export default function ShapeDividerControls({
    shape = 'tilt',
    colorFill = 'primary',
    flip = '',
    onChange,
    onRemove,
}) {
    const normalizedShape = React.useMemo(() => {
        const clean = String(shape ?? '').trim();
        if (clean === SHAPE_NONE_VALUE) return SHAPE_NONE_VALUE;
        return SHAPE_OPTIONS.includes(clean) ? clean : 'tilt';
    }, [shape]);

    const shapeGroup = React.useMemo(
        () => [
            {
                label: 'Shapes',
                options: [
                    { value: SHAPE_NONE_VALUE, label: 'None' },
                    ...SHAPE_OPTIONS.map((opt) => ({
                        value: opt,
                        label: titleize(opt),
                    })),
                ],
            },
        ],
        []
    );

    const flatColorValues = React.useMemo(
        () =>
            COLOR_FILL_GROUPS.flatMap((g) =>
                (g.options ?? []).map((o) => o.value)
            ),
        []
    );

    const normalizedColor = React.useMemo(() => {
        const clean = String(colorFill ?? '').trim();
        return flatColorValues.includes(clean) ? clean : 'primary';
    }, [colorFill, flatColorValues]);

    const normalizedFlip = String(flip ?? '').trim() === 'true' ? 'true' : '';

    const emit = (key, value) => onChange?.(key, value);
    const handleShapeChange = (value) => {
        if (value === SHAPE_NONE_VALUE) {
            onRemove?.();
            return;
        }
        emit('Shape', value);
    };

    return (
        <div>
            <div className="space-y-4">
                <div className="md:max-w-xl">
                    <label className="block text-xs font-semibold leading-5 text-primary-700">
                        Shape
                    </label>
                    <div className="mt-1">
                        <ColorSchemeSelect
                            value={normalizedShape}
                            onChange={handleShapeChange}
                            groups={shapeGroup}
                            swatchFn={() => 'bg-neutral-200'}
                            renderSwatch={(option) => {
                                if (option.value === SHAPE_NONE_VALUE) {
                                    return (
                                        <span className="flex h-4 w-8 items-center justify-center rounded bg-neutral-100 ring-1 ring-neutral-200">
                                            <span className="h-px w-4 rounded bg-neutral-400" />
                                        </span>
                                    );
                                }
                                const shapeDef =
                                    svgShapes[option.value] || svgShapes.tilt;
                                const { path, viewbox } = shapeDef;
                                return (
                                    <span className="flex h-4 w-8 items-center justify-center rounded bg-neutral-100 ring-1 ring-neutral-200">
                                        <svg
                                            viewBox={viewbox}
                                            className="h-full w-full text-neutral-500"
                                            preserveAspectRatio="none"
                                            aria-hidden="true"
                                        >
                                            {path && (
                                                <path
                                                    d={path}
                                                    fill="currentColor"
                                                />
                                            )}
                                        </svg>
                                    </span>
                                );
                            }}
                        />
                    </div>
                </div>

                <div className="md:max-w-xl">
                    <label className="block text-xs font-semibold leading-5 text-primary-700">
                        Fill Color
                    </label>
                    <div className="mt-1">
                        <ColorSchemeSelect
                            value={normalizedColor}
                            onChange={(val) => emit('ColorFill', val)}
                            groups={COLOR_FILL_GROUPS}
                            swatchFn={fillColorToSwatch}
                        />
                    </div>
                </div>

                <div className="md:max-w-xs">
                    <div className="flex items-center gap-3 py-2">
                        <span className="text-xs font-semibold leading-5 text-primary-700">
                            Flip
                        </span>
                        <Toggle
                            checked={normalizedFlip === 'true'}
                            ariaLabel="Flip divider"
                            onChange={(checked) =>
                                emit('Flip', checked ? 'true' : '')
                            }
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
